/**
 * Utilidades para cumpleañeros del mes (fecha de nacimiento en ficha de miembro).
 * Tolera varios formatos de texto y omite valores vacíos o inválidos.
 */

function isLeapYear(y) {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
}

/** Fecha local a medianoche (solo año-mes-día). */
function startOfLocalDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * @param {unknown} raw
 * @returns {Date | null}
 */
export function parseBirthDate(raw) {
  if (raw == null) return null;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw;
  const s = String(raw).trim();
  if (!s) return null;

  const safe = (y, mo, d) => {
    const dt = new Date(y, mo - 1, d);
    if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
    return dt;
  };

  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s].*)?$/);
  if (m) return safe(+m[1], +m[2], +m[3]);

  m = s.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
  if (m) return safe(+m[1], +m[2], +m[3]);

  m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (m) {
    const d = +m[1];
    const mo = +m[2];
    const y = +m[3];
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) return safe(y, mo, d);
  }

  m = s.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (m) return safe(+m[1], +m[2], +m[3]);

  return null;
}

/**
 * Fecha de celebración en el año `y` (29 feb → 28 feb si el año no es bisiesto).
 * @param {Date} birth
 * @param {number} y
 * @returns {Date}
 */
export function celebrationDateInYear(birth, y) {
  const mo = birth.getMonth();
  const d = birth.getDate();
  if (mo === 1 && d === 29 && !isLeapYear(y)) {
    return new Date(y, 1, 28);
  }
  return new Date(y, mo, d);
}

/**
 * Edad que cumple en su cumpleaños de este año calendario.
 * @param {Date} birth
 * @param {number} year
 */
export function ageTurningThisYear(birth, year) {
  return year - birth.getFullYear();
}

/**
 * Diferencia en días entre dos fechas locales (medianoche).
 * @param {Date} a
 * @param {Date} b
 */
function diffCalendarDays(a, b) {
  const ms = 86400000;
  return Math.round((startOfLocalDay(a).getTime() - startOfLocalDay(b).getTime()) / ms);
}

/**
 * @param {number} diffDays celebration - hoy
 */
export function formatBirthdayDaysLabel(diffDays) {
  if (diffDays === 0) return "Hoy";
  if (diffDays > 0) {
    return diffDays === 1 ? "En 1 día" : `En ${diffDays} días`;
  }
  const ago = -diffDays;
  return ago === 1 ? "Hace 1 día" : `Hace ${ago} días`;
}

/**
 * Orden: hoy primero; luego fechas futuras del mes (día ascendente); al final las ya pasadas (día ascendente).
 * No ordena alfabéticamente: usa grupo + día del mes + nombre como desempate.
 *
 * @param {Array<Record<string, unknown>>} members
 * @param {(cellId: string) => string} getCellName
 * @param {Date} [now]
 * @returns {{
 *   monthTitle: string,
 *   sortedRows: Array<{
 *     member: object,
 *     cellName: string,
 *     displayDate: string,
 *     displayDateShort: string,
 *     ageTurning: number | null,
 *     celebrationDay: number,
 *     celebrationDate: Date,
 *     diffDays: number,
 *     daysLabel: string,
 *     isToday: boolean,
 *     isUpcomingWithin3Days: boolean,
 *   }>,
 * }}
 */
export function buildMonthBirthdays(members, getCellName, now = new Date()) {
  const year = now.getFullYear();
  const monthIndex = now.getMonth();
  const todayDay = now.getDate();

  const monthTitle = now.toLocaleDateString("es-MX", { month: "long", year: "numeric" });

  /** @type {Array<{ member: object, cellName: string, displayDate: string, displayDateShort: string, ageTurning: number | null, celebrationDay: number, celebrationDate: Date, diffDays: number, daysLabel: string, isToday: boolean, isUpcomingWithin3Days: boolean, sortGroup: number, sortDay: number, sortName: string }>} */
  const pool = [];

  for (const member of members || []) {
    const birth = parseBirthDate(member.birth_date);
    if (!birth) continue;

    const celeb = celebrationDateInYear(birth, year);
    if (celeb.getMonth() !== monthIndex) continue;

    const celebrationDay = celeb.getDate();
    const diffDays = diffCalendarDays(celeb, now);
    const daysLabel = formatBirthdayDaysLabel(diffDays);
    const isToday = diffDays === 0;
    const isUpcomingWithin3Days = diffDays >= 1 && diffDays <= 3;

    const displayDate = celeb.toLocaleDateString("es-MX", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
    const displayDateShort = celeb.toLocaleDateString("es-MX", {
      day: "numeric",
      month: "long",
    });

    const ageRaw = ageTurningThisYear(birth, year);
    const ageTurning =
      Number.isFinite(ageRaw) && ageRaw >= 0 ? ageRaw : null;

    const cellName = typeof getCellName === "function" ? getCellName(member.cell_id) : "—";
    const name = String(member.name || "").trim() || "Sin nombre";

    let sortGroup;
    if (diffDays === 0) sortGroup = 0;
    else if (diffDays > 0) sortGroup = 1;
    else sortGroup = 2;

    pool.push({
      member,
      cellName,
      displayDate,
      displayDateShort,
      ageTurning,
      celebrationDay,
      celebrationDate: celeb,
      diffDays,
      daysLabel,
      isToday,
      isUpcomingWithin3Days,
      sortGroup,
      sortDay: celebrationDay,
      sortName: name.toLocaleLowerCase("es"),
    });
  }

  pool.sort((a, b) => {
    if (a.sortGroup !== b.sortGroup) return a.sortGroup - b.sortGroup;
    if (a.sortDay !== b.sortDay) return a.sortDay - b.sortDay;
    return a.sortName.localeCompare(b.sortName, "es");
  });

  const sortedRows = pool.map(
    ({
      member,
      cellName,
      displayDate,
      displayDateShort,
      ageTurning,
      celebrationDay,
      celebrationDate,
      diffDays,
      daysLabel,
      isToday,
      isUpcomingWithin3Days,
    }) => ({
      member,
      cellName,
      displayDate,
      displayDateShort,
      ageTurning: ageTurning != null && ageTurning >= 1 ? ageTurning : null,
      celebrationDay,
      celebrationDate,
      diffDays,
      daysLabel,
      isToday,
      isUpcomingWithin3Days,
    })
  );

  return { monthTitle, sortedRows };
}
